import { formatDate } from "@/features/finance/lib/finance-utils"
import { formatKpiCurrency, type DisplayAmount } from "@/shared/lib/formatting/currency"
import { isMoneyString } from "@ibada/shared-types/finance"
import { OverviewValueFormat } from "@ibada/shared-types/overview"

/**
 * For genuine NUMBERS on an overview card or table cell — counts, percentages, ratios.
 *
 * **Not for money.** An overview card whose `valueFormat` is `Money` carries a canonical decimal string
 * (IBA-116); putting it through `Number()` is what silently rounded a cent off large amounts. Use
 * {@link readMoney} for those.
 */
export function coerceNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/**
 * Reads a money value off an untyped overview payload, WITHOUT converting it.
 *
 * `OverviewCard.value` and table cells are `unknown` by contract (every module fills them differently), so the
 * money still has to be recognised — but recognising it is not the same as coercing it. The decimal string goes
 * to `Intl` intact, which parses it exactly.
 */
export function readMoney(value: unknown): DisplayAmount | null {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return isMoneyString(trimmed) ? trimmed : null
  }
  if (typeof value === "number" && Number.isFinite(value)) return value
  return null
}

export function formatOverviewCardDisplayValue(
  value: unknown,
  valueFormat: OverviewValueFormat,
  currencyCode: string,
  locale: string,
): string {
  if (value === null || value === "") return "—"

  if (valueFormat === OverviewValueFormat.Text) {
    return String(value)
  }

  if (valueFormat === OverviewValueFormat.Date) {
    const asString = typeof value === "string" ? value : String(value)
    return formatDate(asString) ?? asString
  }

  if (valueFormat === OverviewValueFormat.Percentage) {
    const n = coerceNumber(value)
    if (n === null) return "—"
    return `${n.toLocaleString(locale, { maximumFractionDigits: 2 })}%`
  }

  if (valueFormat === OverviewValueFormat.Money) {
    const amount = readMoney(value)
    if (amount === null) return "—"
    // The decimal STRING is handed to Intl untouched — it parses it exactly, so `"1500.00"` still renders
    // `$1,500.00` and a cent on a very large amount is no longer rounded away on the way to the card.
    // KPI cards switch to compact notation ($1.2M, 2,5 Md €) at and above 1,000,000 so long amounts don't
    // blow up the card layout.
    return formatKpiCurrency(amount, currencyCode, locale)
  }

  const n = coerceNumber(value)
  if (n === null) return String(value)
  return n.toLocaleString(locale, { maximumFractionDigits: 2 })
}
